import { useState } from 'react';
import { XMarkIcon, PhotoIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  useCreateMaintenanceInfaq,
} from '../../hooks/useMaintenanceInfaq';
import { maintenanceInfaqService } from '../../services/maintenance-infaq.service';

interface Props {
  assetId: string;
  assetName: string;
  onDone: (created: boolean) => void;
  onClose: () => void;
}

const INVOICE_THRESHOLD = 250;

export default function MaintenanceInfaqModal({
  assetId,
  assetName,
  onDone,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const createMutation = useCreateMaintenanceInfaq();

  const [step, setStep] = useState<'ask' | 'form'>('ask');
  const [form, setForm] = useState({
    title: '',
    reason: '',
    description: '',
    targetAmount: 0,
    tireChange: false,
  });
  const [damagePhotoFiles, setDamagePhotoFiles] = useState<File[]>([]);
  const [invoiceFiles, setInvoiceFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const requiresInvoice = form.tireChange || form.targetAmount > INVOICE_THRESHOLD;

  const uploadMany = async (files: File[]): Promise<string[]> => {
    const urls: string[] = [];
    for (const f of files) {
      urls.push(await maintenanceInfaqService.uploadFile(f));
    }
    return urls;
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.reason.trim() || !form.description.trim()) {
      toast.error('Please fill in title, reason and description');
      return;
    }
    if (form.targetAmount <= 0) {
      toast.error('Target amount must be greater than zero');
      return;
    }
    if (requiresInvoice && invoiceFiles.length === 0) {
      toast.error(
        t('maintenanceInfaq.errors.invoiceRequired', {
          defaultValue:
            'An invoice or quote is required when target exceeds RM250 or tire change is selected',
        }),
      );
      return;
    }

    try {
      setUploading(true);
      const [damagePhotoUrls, submissionInvoiceUrls] = await Promise.all([
        uploadMany(damagePhotoFiles),
        uploadMany(invoiceFiles),
      ]);
      setUploading(false);

      await createMutation.mutateAsync({
        assetId,
        title: form.title.trim(),
        reason: form.reason.trim(),
        description: form.description.trim(),
        targetAmount: Number(form.targetAmount),
        tireChange: form.tireChange,
        damagePhotoUrls,
        submissionInvoiceUrls,
      });

      onDone(true);
    } catch (e) {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <div className="my-8 w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-neutral-200 p-5">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              {t('maintenanceInfaq.title', { defaultValue: 'Maintenance Infaq' })}
            </h2>
            <p className="text-xs text-neutral-500">
              {assetName} · {t('maintenanceInfaq.asset', { defaultValue: 'Asset' })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-neutral-400 hover:bg-neutral-100"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {step === 'ask' ? (
          <div className="space-y-5 p-5">
            <p className="text-sm text-neutral-700">
              {t('maintenanceInfaq.askIntro', {
                defaultValue:
                  'This asset is being moved to maintenance. Would you like to open a public donation campaign (infaq) to help cover repair costs?',
              })}
            </p>
            <ul className="list-disc space-y-1 pl-5 text-xs text-neutral-600">
              <li>
                {t('maintenanceInfaq.askBullet1', {
                  defaultValue:
                    'Campaigns require admin approval before going live.',
                })}
              </li>
              <li>
                {t('maintenanceInfaq.askBullet2', {
                  defaultValue:
                    'Maximum 3-month run; unused funds remain with platform.',
                })}
              </li>
              <li>
                {t('maintenanceInfaq.askBullet3', {
                  defaultValue:
                    'Asset stays locked until closing report is approved.',
                })}
              </li>
            </ul>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
              >
                {t('common.no', { defaultValue: 'No, skip' })}
              </button>
              <button
                onClick={() => setStep('form')}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                {t('maintenanceInfaq.openCampaign', { defaultValue: 'Yes, open campaign' })}
              </button>
            </div>
          </div>
        ) : (
          <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
            <Field label={t('maintenanceInfaq.titleLabel', { defaultValue: 'Campaign title *' })}>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                placeholder={t('maintenanceInfaq.titlePlaceholder', {
                  defaultValue: 'e.g., Engine overhaul for Ambulance 1',
                })}
              />
            </Field>

            <Field
              label={t('maintenanceInfaq.reasonLabel', { defaultValue: 'Reason *' })}
              hint={t('maintenanceInfaq.reasonHint', {
                defaultValue: 'Short summary shown on the campaign card.',
              })}
            >
              <input
                type="text"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
            </Field>

            <Field
              label={t('maintenanceInfaq.descriptionLabel', { defaultValue: 'Description *' })}
            >
              <textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field
                label={t('maintenanceInfaq.targetLabel', { defaultValue: 'Target amount (RM) *' })}
              >
                <input
                  type="number"
                  min={1}
                  step="0.01"
                  value={form.targetAmount || ''}
                  onChange={(e) =>
                    setForm({ ...form, targetAmount: parseFloat(e.target.value) || 0 })
                  }
                  className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
                />
              </Field>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm text-neutral-700">
                  <input
                    type="checkbox"
                    checked={form.tireChange}
                    onChange={(e) => setForm({ ...form, tireChange: e.target.checked })}
                    className="h-4 w-4 rounded border-neutral-300"
                  />
                  {t('maintenanceInfaq.tireChange', { defaultValue: 'Tire change / major part' })}
                </label>
              </div>
            </div>

            {requiresInvoice && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                {t('maintenanceInfaq.invoiceTierNote', {
                  defaultValue:
                    'Invoice tier: a quote or invoice is required at submission. Actual receipts will be required in the closing report.',
                })}
              </div>
            )}

            <FileInput
              label={t('maintenanceInfaq.damagePhotos', { defaultValue: 'Damage photos (optional)' })}
              icon={<PhotoIcon className="h-4 w-4" />}
              files={damagePhotoFiles}
              onChange={setDamagePhotoFiles}
              accept="image/*"
            />

            <FileInput
              label={
                requiresInvoice
                  ? t('maintenanceInfaq.invoiceRequired', { defaultValue: 'Quote / invoice (required)' })
                  : t('maintenanceInfaq.invoiceOptional', { defaultValue: 'Quote / invoice (optional)' })
              }
              icon={<PaperClipIcon className="h-4 w-4" />}
              files={invoiceFiles}
              onChange={setInvoiceFiles}
              accept="image/*,application/pdf"
              required={requiresInvoice}
            />

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setStep('ask')}
                className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
              >
                {t('common.back', { defaultValue: 'Back' })}
              </button>
              <button
                onClick={handleSubmit}
                disabled={createMutation.isPending || uploading}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {createMutation.isPending || uploading
                  ? t('common.submitting', { defaultValue: 'Submitting…' })
                  : t('maintenanceInfaq.submit', { defaultValue: 'Submit for review' })}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-800 mb-1">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
    </div>
  );
}

function FileInput({
  label,
  icon,
  files,
  onChange,
  accept,
  required,
}: {
  label: string;
  icon: React.ReactNode;
  files: File[];
  onChange: (f: File[]) => void;
  accept?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-neutral-800">
        {label}
        {required && <span className="ml-1 text-red-600">*</span>}
      </label>
      <div className="flex items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
          {icon}
          <span>Choose files</span>
          <input
            type="file"
            accept={accept}
            multiple
            className="hidden"
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              onChange([...files, ...picked]);
              e.target.value = '';
            }}
          />
        </label>
        <span className="text-xs text-neutral-500">
          {files.length ? `${files.length} file(s) selected` : 'No files selected'}
        </span>
      </div>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between rounded bg-neutral-50 px-2 py-1 text-xs">
              <span className="truncate">{f.name}</span>
              <button
                onClick={() => onChange(files.filter((_, idx) => idx !== i))}
                className="ml-2 text-red-600 hover:underline"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
